import { ErrorResponse } from '../types/common'
// Determine default API URL based on current environment
const getDefaultApiUrl = () => {
  if (typeof window === 'undefined') return 'http://localhost:3001/api/v1'
  const protocol = window.location.protocol
  const hostname = window.location.hostname
  const port = hostname === 'localhost' ? ':3001' : ''
  return `${protocol}//${hostname}${port}/api/v1`
}
// Safely access environment variable with fallback
const API_BASE_URL = (import.meta?.env?.VITE_API_BASE_URL as string) || getDefaultApiUrl()
class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: Record<string, string>,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}
const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json()
    throw new ApiError(
      error.message || 'An unexpected error occurred',
      response.status,
      error.code,
      error.details,
    )
  }
  return response.json()
}
const handleError = (error: unknown): ErrorResponse => {
  if (error instanceof ApiError) {
    return {
      message: error.message,
      code: error.code,
      details: error.details,
    }
  }
  return {
    message: error instanceof Error ? error.message : 'An unexpected error occurred',
  }
}
export const api = {
  get: async <T>(endpoint: string, params?: Record<string, string>): Promise<T> => {
    try {
      const url = new URL(`${API_BASE_URL}${endpoint}`)
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          url.searchParams.append(key, value)
        })
      }
      const response = await fetch(url.toString(), {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      return handleResponse<T>(response)
    } catch (error) {
      throw handleError(error)
    }
  },
  post: async <T>(endpoint: string, data: unknown): Promise<T> => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
      return handleResponse<T>(response)
    } catch (error) {
      throw handleError(error)
    }
  },
  put: async <T>(endpoint: string, data: unknown): Promise<T> => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
      return handleResponse<T>(response)
    } catch (error) {
      throw handleError(error)
    }
  },
  delete: async <T>(endpoint: string): Promise<T> => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      return handleResponse<T>(response)
    } catch (error) {
      throw handleError(error)
    }
  },
}